import { useCallback, useMemo } from 'react';
import CheckedIcon from 'src/assets/icons/CheckedIcon';
import Button from 'src/components/design-system/Button';
import DocumentCountTracker from 'src/components/DocumentCountTracker';
import Image from 'src/components/Image';
import PreviewPDF from 'src/components/PreviewPDF';
import useInvoiceScanManager from 'src/hooks/useInvoiceScanManager';
import useWindowSize from 'src/hooks/useWindowSize';
import { validSignedUrl } from 'src/utils/strings';
import FailedStatusPreview from './ocr/FailedStatusPreview';

const InvoicePreviewSection = () => {
  const { height, width } = useWindowSize();
  const {
    count,
    setAttachmentListData,
    setOcrAttachmentList,
    index,
    previewDocument,
    setDataState,
    setOcrDataState,
    dataState,
    ocrDataState,
    setIsDecrement,
    selectedView,
    openOcrReviewModal,
    isOcrCompany,
  } = useInvoiceScanManager();

  const document = previewDocument?.signedUrl;
  const isPDF = useMemo(
    () => previewDocument?.description?.toLowerCase().endsWith('.pdf'),
    [previewDocument?.description]
  );

  const decrementSelectedIndex = useCallback(() => {
    setAttachmentListData((prev) => {
      const previousAttachments = [...prev];
      previousAttachments[index].preview = false;
      if (index !== 0) {
        previousAttachments[index - 1].preview = true;
      } else {
        setDataState((prev) => ({ ...prev, skip: (prev.skip || 0) - (prev.take || 0) }));
        setIsDecrement(true);
      }
      return previousAttachments;
    });
  }, [index, setAttachmentListData, setDataState, setIsDecrement]);

  const incrementSelectedIndex = useCallback(() => {
    setAttachmentListData((prev) => {
      const previousAttachments = [...prev];
      previousAttachments[index].preview = false;
      if ((dataState?.take || 0) - 1 !== index) {
        previousAttachments[index + 1].preview = true;
      } else {
        setDataState((prev) => ({ ...prev, skip: (prev.skip || 0) + (prev.take || 0) }));
        setIsDecrement(false);
      }
      return previousAttachments;
    });
  }, [dataState?.take, index, setAttachmentListData, setDataState, setIsDecrement]);

  // Separate Function for OCR
  const decrementSelectedOcrIndex = useCallback(() => {
    setOcrAttachmentList((prev) => {
      const previousAttachments = [...prev];
      previousAttachments[index].preview = false;
      if (index !== 0) {
        previousAttachments[index - 1].preview = true;
      } else {
        setOcrDataState((prev) => ({ ...prev, skip: (prev.skip || 0) - (prev.take || 0) }));
        setIsDecrement(true);
      }
      return previousAttachments;
    });
  }, [index, setOcrAttachmentList, setOcrDataState, setIsDecrement]);

  //Separate Function for OCR
  const incrementSelectedOcrIndex = useCallback(() => {
    setOcrAttachmentList((prev) => {
      const previousAttachments = [...prev];
      previousAttachments[index].preview = false;
      if ((ocrDataState?.take || 0) - 1 !== index) {
        previousAttachments[index + 1].preview = true;
      } else {
        setOcrDataState((prev) => ({ ...prev, skip: (prev.skip || 0) + (prev.take || 0) }));
        setIsDecrement(false);
      }
      return previousAttachments;
    });
  }, [ocrDataState?.take, index, setOcrAttachmentList, setOcrDataState, setIsDecrement]);

  const disableDecValidation =
    selectedView === 0
      ? index === 0 && dataState.skip === 0
      : index === 0 && ocrDataState.skip === 0;
  const disableIncValidation =
    selectedView === 0
      ? (dataState.skip || 0) + index + 1 === count
      : (ocrDataState.skip || 0) + index + 1 === count;
  const currentSelectedValidation =
    selectedView === 0 ? (dataState?.skip || 0) + index + 1 : (ocrDataState?.skip || 0) + index + 1;

  const { previewHeight, isOcrFailed, isNeedsReview } = useMemo(() => {
    const isFailed = selectedView === 1 && previewDocument?.ocrStatus === 'Failed';
    const isNeedsReview = selectedView === 1 && previewDocument?.ocrStatus === 'Needs Review';
    const previewHeight = isFailed ? height - 346 : isNeedsReview ? height - 220 : height - 135;

    return {
      previewHeight,
      isOcrFailed: isFailed,
      isNeedsReview: isNeedsReview,
    };
  }, [height, previewDocument?.ocrStatus, selectedView]);

  return (
    <div>
      <div>
        <DocumentCountTracker
          disableDecrement={disableDecValidation}
          disableIncrement={disableIncValidation}
          total={count}
          currentSelected={currentSelectedValidation}
          incrementFn={selectedView === 0 ? incrementSelectedIndex : incrementSelectedOcrIndex}
          decrementFn={selectedView === 0 ? decrementSelectedIndex : decrementSelectedOcrIndex}
          isOcr={true}
        />

        {isNeedsReview && (
          <div className="flex justify-center items-center m-1 flex-col gap-1">
            <div className="flex flex-row gap-1 p-2 rounded-3xl bg-[#ddf0c8] items-center justify-center">
              <CheckedIcon width="14" height="14" />
              <span className="text-xs font-[500]">Scan Complete</span>
            </div>
            <Button
              color="secondary"
              onClick={() => {
                openOcrReviewModal(previewDocument?.attachId as number);
              }}
            >
              Review Scan
            </Button>
          </div>
        )}
        {isOcrFailed && <FailedStatusPreview />}
        <div style={{ height: `${previewHeight}px`, overflowY: 'auto' }}>
          {!isPDF && (
            <Image
              src={`${validSignedUrl(document as string)}`}
              controls
              filename={previewDocument?.originalFile}
              styles={{
                width: `${width - 20}px`,
              }}
              isOcr={isOcrCompany}
            />
          )}
          {isPDF && (
            <PreviewPDF
              filename={previewDocument?.originalFile}
              url={`${validSignedUrl(document as string)}`}
              height={`${previewHeight}px`}
              isOcr={isOcrCompany}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default InvoicePreviewSection;
